/**
 * Weather Service
 * Fetches weather data from OpenWeatherMap API (or fallback to other services)
 */

export interface WeatherData {
  conditions: string;
  temperature: number;
  temperature_high?: number;
  temperature_low?: number;
  wind_speed?: number;
  wind_direction?: string;
  humidity?: number;
  description: string;
}

class WeatherService {
  private apiKey: string = ''; // Will be set from environment or config
  private baseUrl: string = 'https://api.openweathermap.org/data/2.5/weather';

  /**
   * Set API key (should be called on app initialization)
   */
  setApiKey(key: string) {
    this.apiKey = key;
  }

  /**
   * Get current weather by coordinates
   */
  async getWeatherByCoordinates(lat: number, lon: number): Promise<WeatherData | null> {
    // If no API key, try to use browser geolocation with free weather API
    if (!this.apiKey) {
      return this.getWeatherFromFreeAPI(lat, lon);
    }

    try {
      const url = `${this.baseUrl}?lat=${lat}&lon=${lon}&appid=${this.apiKey}&units=metric`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Weather API request failed');
      }

      const data = await response.json();
      return this.parseOpenWeatherMapData(data);
    } catch (error) {
      console.error('Error fetching weather from OpenWeatherMap:', error);
      return this.getWeatherFromFreeAPI(lat, lon);
    }
  }

  /**
   * Get current weather by city name
   */
  async getWeatherByCity(city: string): Promise<WeatherData | null> {
    if (!this.apiKey) {
      console.warn('No weather API key set. Using fallback service.');
      return null;
    }

    try {
      const url = `${this.baseUrl}?q=${encodeURIComponent(city)}&appid=${this.apiKey}&units=metric`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Weather API request failed');
      }

      const data = await response.json();
      return this.parseOpenWeatherMapData(data);
    } catch (error) {
      console.error('Error fetching weather by city:', error);
      return null;
    }
  }

  /**
   * Get weather using browser's geolocation
   */
  async getWeatherForCurrentLocation(): Promise<WeatherData | null> {
    return new Promise((resolve) => {
      if (!navigator.geolocation) {
        console.error('Geolocation not supported');
        resolve(null);
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;
          const weather = await this.getWeatherByCoordinates(latitude, longitude);
          resolve(weather);
        },
        (error) => {
          console.error('Geolocation error:', error);
          resolve(null);
        }
      );
    });
  }

  /**
   * Get weather from free API (Open-Meteo - no API key required)
   */
  private async getWeatherFromFreeAPI(lat: number, lon: number): Promise<WeatherData | null> {
    try {
      const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true&daily=temperature_2m_max,temperature_2m_min&temperature_unit=celsius&timezone=auto`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Free weather API request failed');
      }

      const data = await response.json();
      return this.parseOpenMeteoData(data);
    } catch (error) {
      console.error('Error fetching weather from Open-Meteo:', error);
      return null;
    }
  }

  /**
   * Parse OpenWeatherMap API response
   */
  private parseOpenWeatherMapData(data: any): WeatherData {
    const weatherCode = data.weather[0].id;
    const conditions = this.getWeatherCondition(weatherCode, data.weather[0].main);

    return {
      conditions,
      temperature: Math.round(data.main.temp),
      temperature_high: Math.round(data.main.temp_max),
      temperature_low: Math.round(data.main.temp_min),
      wind_speed: data.wind.speed,
      wind_direction: this.getWindDirection(data.wind.deg),
      humidity: data.main.humidity,
      description: data.weather[0].description
    };
  }

  /**
   * Parse Open-Meteo API response
   */
  private parseOpenMeteoData(data: any): WeatherData {
    const current = data.current_weather;
    const conditions = this.getWeatherConditionFromWMO(current.weathercode);

    // Get today's min/max temps from daily forecast
    const daily = data.daily;
    const tempHigh = daily?.temperature_2m_max?.[0];
    const tempLow = daily?.temperature_2m_min?.[0];

    return {
      conditions,
      temperature: Math.round(current.temperature),
      temperature_high: tempHigh ? Math.round(tempHigh) : undefined,
      temperature_low: tempLow ? Math.round(tempLow) : undefined,
      wind_speed: current.windspeed,
      wind_direction: this.getWindDirection(current.winddirection),
      description: conditions
    };
  }

  /**
   * Map OpenWeatherMap weather code to simple condition
   */
  private getWeatherCondition(code: number, main: string): string {
    if (code >= 200 && code < 300) return 'Thunderstorm';
    if (code >= 300 && code < 400) return 'Light Rain';
    if (code >= 500 && code < 600) {
      if (code >= 502) return 'Heavy Rain';
      return 'Light Rain';
    }
    if (code >= 600 && code < 700) return 'Snow';
    if (code >= 701 && code < 800) return 'Cloudy';
    if (code === 800) return 'Clear';
    if (code === 801 || code === 802) return 'Partly Cloudy';
    if (code > 802) return 'Cloudy';
    return main;
  }

  /**
   * Map WMO weather code to condition (for Open-Meteo)
   */
  private getWeatherConditionFromWMO(code: number): string {
    if (code === 0) return 'Clear';
    if (code === 1 || code === 2) return 'Partly Cloudy';
    if (code === 3) return 'Cloudy';
    if (code >= 45 && code <= 48) return 'Foggy';
    if (code >= 51 && code <= 57) return 'Light Rain';
    if (code >= 61 && code <= 67) return 'Heavy Rain';
    if (code >= 71 && code <= 77) return 'Snow';
    if (code >= 80 && code <= 82) return 'Heavy Rain';
    if (code >= 85 && code <= 86) return 'Snow';
    if (code >= 95 && code <= 99) return 'Thunderstorm';
    return 'Cloudy';
  }

  /**
   * Convert wind degrees to direction
   */
  private getWindDirection(degrees: number): string {
    const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
    const index = Math.round(degrees / 22.5) % 16;
    return directions[index];
  }

  /**
   * Format wind conditions for display
   */
  formatWindConditions(data: WeatherData): string {
    if (!data.wind_speed) return '';
    const speed = Math.round(data.wind_speed * 3.6); // Convert m/s to km/h
    return `${speed} km/h from ${data.wind_direction || 'variable'}`;
  }

  /**
   * Get site conditions suggestion based on weather
   */
  getSiteConditionsSuggestion(data: WeatherData): string {
    const conditions = data.conditions.toLowerCase();

    if (conditions.includes('rain') || conditions.includes('storm')) {
      return 'Wet conditions, limited outdoor work';
    }
    if (conditions.includes('snow')) {
      return 'Snowy conditions, outdoor work restricted';
    }
    if (conditions.includes('clear') || conditions.includes('partly cloudy')) {
      return 'Good working conditions';
    }
    if (conditions.includes('cloudy')) {
      return 'Overcast, suitable for work';
    }

    return 'Normal site conditions';
  }
}

// Export singleton instance
export const weatherService = new WeatherService();
